import React, { useState } from "react";
import { Modal, Form, Alert, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faExclamationTriangle, faSpinner } from "@fortawesome/free-solid-svg-icons";
import Button from "components/base/Button";
import { useFinalizarBalanco, useDepartamentos } from "hooks/useBalanco";
import { DepartamentoType, ZerarEstoqueDto } from "services/balancoService";

interface BotaoFinalizarBalancoProps {
    disabled?: boolean;
}

const BotaoFinalizarBalanco: React.FC<BotaoFinalizarBalancoProps> = ({ disabled = false }) => {
    const [showModal, setShowModal] = useState(false);
    const [zerarEstoque, setZerarEstoque] = useState<'nao' | 'todos' | 'departamentos' | 'controle-especial'>('nao');
    const [departamentosSelecionados, setDepartamentosSelecionados] = useState<number[]>([]);

    const finalizarMutation = useFinalizarBalanco();

    // Só executar a query de departamentos quando a modal estiver aberta E a opção de departamentos estiver selecionada
    const shouldLoadDepartamentos = showModal && zerarEstoque === 'departamentos';
    const departamentosQuery = useDepartamentos(shouldLoadDepartamentos);

    const departamentos = departamentosQuery.data || [];

    const handleFinalizar = async () => {
        try {
            let zerarEstoqueDto: ZerarEstoqueDto | null = null;

            if (zerarEstoque !== 'nao') {
                zerarEstoqueDto = {
                    zerarTodos: zerarEstoque === 'todos',
                    apenasControleEspecial: zerarEstoque === 'controle-especial',
                    departamentosIds: zerarEstoque === 'departamentos' ? departamentosSelecionados : []
                };

                // Validações
                if (zerarEstoque === 'departamentos' && departamentosSelecionados.length === 0) {
                    return;
                }
            }

            await finalizarMutation.mutateAsync(zerarEstoqueDto);
            setShowModal(false);
        } catch (error: any) {
            console.error('Erro ao finalizar balanço:', error);
        }
    };

    const handleDepartamentoChange = (departamentoId: number, checked: boolean) => {
        if (checked) {
            setDepartamentosSelecionados(prev => [...prev, departamentoId]);
        } else {
            setDepartamentosSelecionados(prev => prev.filter(id => id !== departamentoId));
        }
    };

    const isLoading = finalizarMutation.isPending;

    return (
        <>
            <Button
                variant="success"
                onClick={() => setShowModal(true)}
                disabled={disabled || isLoading}
                title="Finalizar balanço"
            >
                <FontAwesomeIcon icon={faCheck} className="me-2" />
                Finalizar Balanço
            </Button>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faCheck} className="me-2 text-success-emphasis" />
                        Finalizar Balanço
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="border border-warning border-opacity-25 bg-warning-subtle mb-4">
                        <div className="text-warning-emphasis">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            <strong>Atenção:</strong> Esta ação irá finalizar o balanço e ajustar os estoques dos produtos incluídos.
                            Após finalizado, o balanço não poderá mais ser modificado.
                        </div>
                    </Alert>

                    <h6 className="mb-3">Deseja zerar algum estoque durante a finalização?</h6>

                    <Form>
                        <Form.Check
                            type="radio"
                            name="zerarEstoque"
                            id="nao-zerar"
                            label="Não zerar nenhum estoque"
                            checked={zerarEstoque === 'nao'}
                            onChange={() => setZerarEstoque('nao')}
                            className="mb-3"
                        />

                        <Form.Check
                            type="radio"
                            name="zerarEstoque"
                            id="zerar-todos"
                            label="Zerar todo o estoque da empresa"
                            checked={zerarEstoque === 'todos'}
                            onChange={() => setZerarEstoque('todos')}
                            className="mb-3"
                        />

                        <Form.Check
                            type="radio"
                            name="zerarEstoque"
                            id="zerar-controle-especial"
                            label="Zerar apenas produtos de controle especial"
                            checked={zerarEstoque === 'controle-especial'}
                            onChange={() => setZerarEstoque('controle-especial')}
                            className="mb-3"
                        />

                        <Form.Check
                            type="radio"
                            name="zerarEstoque"
                            id="zerar-departamentos"
                            label="Zerar estoque por departamentos específicos"
                            checked={zerarEstoque === 'departamentos'}
                            onChange={() => setZerarEstoque('departamentos')}
                            className="mb-3"
                        />

                        {zerarEstoque === 'departamentos' && (
                            <div className="ms-4 mt-3 p-3 bg-body-secondary rounded">
                                <h6 className="mb-3">Selecione os departamentos:</h6>
                                {departamentosQuery.isLoading ? (
                                    <div className="text-center py-3">
                                        <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                        Carregando departamentos...
                                    </div>
                                ) : departamentos.length === 0 ? (
                                    <Alert variant="info" className="border border-info border-opacity-25 bg-info-subtle mb-0">
                                        <div className="text-info-emphasis">
                                            Nenhum departamento encontrado
                                        </div>
                                    </Alert>
                                ) : (
                                    <Row>
                                        {departamentos.map((departamento: DepartamentoType) => (
                                            <Col md={6} key={departamento.id} className="mb-2">
                                                <Form.Check
                                                    type="checkbox"
                                                    id={`dept-${departamento.id}`}
                                                    label={departamento.descricao}
                                                    checked={departamentosSelecionados.includes(departamento.id)}
                                                    onChange={(e) => handleDepartamentoChange(departamento.id, e.target.checked)}
                                                />
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </div>
                        )}
                    </Form>

                    {zerarEstoque !== 'nao' && (
                        <Alert variant="danger" className="border border-danger border-opacity-25 bg-danger-subtle mt-4 mb-0">
                            <div className="text-danger-emphasis">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                <strong>Cuidado:</strong> Esta ação irá zerar permanentemente os estoques selecionados.
                                Esta operação não pode ser desfeita.
                            </div>
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowModal(false)}
                        disabled={isLoading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleFinalizar}
                        disabled={isLoading || (zerarEstoque === 'departamentos' && departamentosSelecionados.length === 0)}
                    >
                        {isLoading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Finalizando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faCheck} className="me-2" />
                                Finalizar Balanço
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BotaoFinalizarBalanco; 